import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import CustomTextArea from "../../../components/formik/CustomTextArea.tsx";
import Button from "../../../components/Button.tsx";
import StatusBadge from "./StatusBadge.tsx";
import PartnerInfoCard from "./PartnerInfoCard.tsx";

interface ClarificationDetailProps {
  clarification: any;
  profileUrl: string;
  statusOptions: any[];
  onSubmit: (values: {
    status: string;
    adminResponse: string;
    customStatus?: string;
  }) => Promise<void>;
  loading: boolean;
}

const ClarificationDetail: React.FC<ClarificationDetailProps> = ({
  clarification,
  profileUrl,
  statusOptions,
  onSubmit,
  loading,
}) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 992);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const validationSchema = Yup.object().shape({
    status: Yup.string().required("Debe seleccionar un estado"),
    customStatus: Yup.string().when("status", {
      is: "Otro",
      then: (schema) =>
        schema.required("Debe especificar el estado personalizado"),
      otherwise: (schema) => schema.optional(),
    }),
    adminResponse: Yup.string().when("status", {
      is: (val: string) =>
        val === "No resuelto" || val === "No procede" || val === "No llego",
      then: (schema) => schema.required("El comentario es obligatorio"),
      otherwise: (schema) => schema.optional(),
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema,
    initialValues: {
      status: clarification?.status || "",
      adminResponse: clarification?.adminResponse || "",
      customStatus: "",
    },
    onSubmit: async (values) => {
      await onSubmit(values);
    },
  });

  const isAlreadyProcessed =
    clarification.status !== "Pendiente" &&
    clarification.status !== "Sin estado";

  return (
    <div style={styles.container}>
      <div style={styles.statusCard}>
        <div style={styles.statusContent}>
          <h5 style={styles.statusTitle}>Estado de la Aclaración</h5>
          <StatusBadge status={clarification.status} />
        </div>
        {isAlreadyProcessed && (
          <div style={styles.infoAlert}>
            <i className="fas fa-info-circle" style={styles.infoIcon}></i>
            Esta aclaración ya ha sido procesada.
          </div>
        )}
      </div>

      <div
        style={{
          ...styles.contentGrid,
          gridTemplateColumns: isMobile ? "1fr" : "minmax(280px, 30%) 1fr",
        }}
      >
        <div style={styles.sidebar}>
          {clarification?.partner && (
            <PartnerInfoCard
              partner={clarification.partner}
              profileUrl={profileUrl}
              checkDate={clarification.checkDate}
            />
          )}
        </div>

        <div style={styles.mainContent}>
          <div style={styles.formCard}>
            <div style={styles.formHeader}>
              <h5 style={styles.formTitle}>
                <i
                  className="fas fa-clipboard-list"
                  style={styles.formIcon}
                ></i>
                Datos de la aclaración
              </h5>
            </div>

            <div style={styles.formBody}>
              <div style={styles.formSection}>
                <label style={styles.label}>
                  <i className="fas fa-comment" style={styles.labelIcon}></i>
                  Comentario del Colaborador
                </label>
                <div style={styles.commentBox}>
                  <p style={styles.commentText}>{clarification.comment}</p>
                </div>
              </div>

              <div style={styles.formSection}>
                <label style={styles.label}>
                  <i className="fas fa-clock" style={styles.labelIcon}></i>
                  Fecha de Solicitud
                </label>
                <div style={styles.dateBox}>
                  {moment(clarification.createdAt).format(
                    "dddd, DD [de] MMMM [de] YYYY [a las] HH:mm"
                  )}
                </div>
              </div>

              {clarification.adminResponse && (
                <div style={styles.formSection}>
                  <label style={styles.label}>
                    <i className="fas fa-reply" style={styles.labelIcon}></i>
                    Respuesta Anterior
                  </label>
                  <div style={styles.responseBox}>
                    {clarification.adminResponse}
                  </div>
                </div>
              )}

              {!isAlreadyProcessed && (
                <>
                  <div style={styles.formSection}>
                    <label style={styles.label}>
                      <i
                        className="fas fa-check-circle"
                        style={styles.labelIcon}
                      ></i>
                      Decisión *
                    </label>
                    <div style={styles.statusGrid}>
                      {statusOptions.map((option) => {
                        const isSelected =
                          formik.values.status === option.value;
                        const colorMap: { [key: string]: string } = {
                          success: "#10b981",
                          danger: "#ef4444",
                          warning: "#f59e0b",
                          info: "#3b82f6",
                          secondary: "#6b7280",
                        };
                        const bgColor = colorMap[option.color] || "#6b7280";

                        return (
                          <div
                            key={option.value}
                            style={{
                              ...styles.statusOption,
                              border: isSelected
                                ? `2px solid ${bgColor}`
                                : "1px solid #e5e7eb",
                              backgroundColor: isSelected
                                ? `${bgColor}15`
                                : "#ffffff",
                            }}
                            onClick={() =>
                              formik.setFieldValue("status", option.value)
                            }
                          >
                            <input
                              type="radio"
                              name="status"
                              id={`status-${option.value}`}
                              value={option.value}
                              checked={isSelected}
                              onChange={() =>
                                formik.setFieldValue("status", option.value)
                              }
                              style={styles.radioInput}
                            />
                            <label
                              htmlFor={`status-${option.value}`}
                              style={styles.statusLabel}
                            >
                              <span
                                style={{
                                  ...styles.statusBadge,
                                  backgroundColor: isSelected
                                    ? bgColor
                                    : "#f3f4f6",
                                  color: isSelected ? "#ffffff" : "#374151",
                                }}
                              >
                                <i
                                  className={`fas ${option.icon}`}
                                  style={styles.statusIcon}
                                ></i>
                                {option.label}
                              </span>
                            </label>
                          </div>
                        );
                      })}
                    </div>
                    {formik.touched.status && formik.errors.status && (
                      <div style={styles.errorText}>{formik.errors.status}</div>
                    )}

                    {formik.values.status === "Otro" && (
                      <div style={styles.customStatusSection}>
                        <label style={styles.label}>
                          <i
                            className="fas fa-edit"
                            style={styles.labelIcon}
                          ></i>
                          Especificar estado personalizado *
                        </label>
                        <input
                          type="text"
                          name="customStatus"
                          value={formik.values.customStatus}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Escribe el estado personalizado..."
                          style={{
                            ...styles.customStatusInput,
                            borderColor:
                              formik.touched.customStatus &&
                              formik.errors.customStatus
                                ? "#dc2626"
                                : formik.values.customStatus
                                ? "#50B7C0"
                                : "#e5e7eb",
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = "#50B7C0";
                            e.currentTarget.style.boxShadow =
                              "0 0 0 3px rgba(80, 183, 192, 0.1)";
                          }}
                          onBlur={(e) => {
                            formik.handleBlur(e);
                            const hasError =
                              formik.touched.customStatus &&
                              formik.errors.customStatus;
                            e.currentTarget.style.borderColor = hasError
                              ? "#dc2626"
                              : formik.values.customStatus
                              ? "#50B7C0"
                              : "#e5e7eb";
                            e.currentTarget.style.boxShadow = "none";
                          }}
                        />
                        {formik.touched.customStatus &&
                          formik.errors.customStatus && (
                            <div style={styles.errorText}>
                              {formik.errors.customStatus}
                            </div>
                          )}
                      </div>
                    )}
                  </div>

                  <div style={styles.formSection}>
                    <CustomTextArea
                      formik={formik}
                      field="adminResponse"
                      label="Respuesta del Administrador"
                      labelRequired={
                        formik.values.status === "No resuelto" ||
                        formik.values.status === "No procede" ||
                        formik.values.status === "No llego"
                      }
                      placeholder="Escribe tu respuesta aquí..."
                    />
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <div style={styles.actionBar}>
        <Button
          onClick={() => navigate(-1)}
          title="Cancelar"
          variant="secondary"
          type="button"
          disabled={loading}
        />
        {!isAlreadyProcessed ? (
          <Button
            onClick={() => formik.handleSubmit()}
            title="Procesar"
            variant="azul"
            disabled={loading}
            type="submit"
          />
        ) : (
          <Button
            onClick={() => navigate(-1)}
            title={`Aclaración ${clarification.status.toLowerCase()}`}
            variant="secondary"
            type="button"
            disabled={true}
          />
        )}
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    padding: "1.5rem",
  },
  statusCard: {
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    padding: "1.5rem",
    marginBottom: "1.5rem",
    border: "1px solid #e5e7eb",
  },
  statusContent: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
  },
  statusTitle: {
    margin: 0,
    fontSize: "1.125rem",
    fontWeight: 600,
    color: "#1f2937",
  },
  infoAlert: {
    background: "#dbeafe",
    border: "1px solid #93c5fd",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    color: "#1e40af",
    fontSize: "0.875rem",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  infoIcon: {
    fontSize: "1rem",
  },
  contentGrid: {
    display: "grid",
    gap: "1.5rem",
    marginBottom: "1.5rem",
  },
  sidebar: {
    minWidth: 0,
  },
  mainContent: {
    minWidth: 0,
  },
  formCard: {
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    overflow: "hidden",
    border: "1px solid #e5e7eb",
    height: "100%",
  },
  formHeader: {
    background: "linear-gradient(135deg, #50B7C0 0%, #36a3ad 100%)",
    padding: "1rem 1.5rem",
  },
  formTitle: {
    margin: 0,
    fontSize: "1.125rem",
    fontWeight: 600,
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  formIcon: {
    fontSize: "1.125rem",
  },
  formBody: {
    padding: "1.5rem",
  },
  formSection: {
    marginBottom: "1.5rem",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    fontSize: "0.875rem",
    fontWeight: 600,
    color: "#374151",
    marginBottom: "0.5rem",
  },
  labelIcon: {
    fontSize: "0.875rem",
    color: "#50B7C0",
  },
  commentBox: {
    background: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "1rem",
  },
  commentText: {
    margin: 0,
    color: "#1f2937",
    lineHeight: 1.6,
  },
  dateBox: {
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    color: "#374151",
    fontSize: "0.875rem",
  },
  responseBox: {
    background: "#f3f4f6",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "0.75rem 1rem",
    color: "#374151",
    fontSize: "0.875rem",
  },
  statusGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: "0.75rem",
  },
  statusOption: {
    padding: "0.75rem",
    borderRadius: "8px",
    cursor: "pointer",
    transition: "all 0.2s ease",
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
  },
  radioInput: {
    cursor: "pointer",
  },
  statusLabel: {
    flex: 1,
    cursor: "pointer",
    margin: 0,
  },
  statusBadge: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
    padding: "0.5rem 0.75rem",
    borderRadius: "8px",
    fontSize: "0.875rem",
    fontWeight: 500,
    border: "none",
    width: "100%",
  },
  statusIcon: {
    fontSize: "0.75rem",
  },
  errorText: {
    color: "#dc2626",
    fontSize: "0.75rem",
    marginTop: "0.5rem",
  },
  customStatusSection: {
    marginTop: "1rem",
    paddingTop: "1rem",
    borderTop: "1px solid #e5e7eb",
  },
  customStatusInput: {
    width: "100%",
    padding: "0.75rem 1rem",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    fontSize: "0.875rem",
    color: "#1f2937",
    backgroundColor: "#ffffff",
    transition: "all 0.2s ease",
    marginTop: "0.5rem",
    outline: "none",
  },
  actionBar: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "1rem",
    background: "#ffffff",
    borderRadius: "12px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06)",
    padding: "1rem 1.5rem",
    border: "1px solid #e5e7eb",
  },
};

export default ClarificationDetail;
